/**
 * API Client
 *
 * Base HTTP client built with Axios
 * Features: interceptors, error handling, retry logic, authentication
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { transformError, shouldRetry, getRetryDelay } from './errors';

import type { ApiClientConfig, ApiResponse, RequestConfig } from './types';

/**
 * Default configuration
 */
const DEFAULT_CONFIG: ApiClientConfig = {
  baseURL: 'http://localhost:3000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
};

/**
 * Token refresh callback type
 * This should return a fresh token, or null if unable to refresh
 */
export type TokenRefreshCallback = () => Promise<string | null>;

/**
 * API Client class
 */
class ApiClient {
  private axiosInstance: AxiosInstance;
  private authToken: string | null = null;
  private config: ApiClientConfig;
  private tokenRefreshCallback: TokenRefreshCallback | null = null;
  private tokenRefreshPromise: Promise<string | null> | null = null;

  constructor(config: ApiClientConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.axiosInstance = axios.create(this.config);
    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - now async to support token refresh
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Refresh token before each request if callback is set
        if (this.tokenRefreshCallback) {
          try {
            // Use a single promise to prevent multiple concurrent refresh calls
            if (!this.tokenRefreshPromise) {
              this.tokenRefreshPromise = this.tokenRefreshCallback();
            }
            const freshToken = await this.tokenRefreshPromise;
            this.tokenRefreshPromise = null;

            if (freshToken) {
              this.authToken = freshToken;
            }
          } catch (error) {
            console.warn('[API] Token refresh failed:', error);
            this.tokenRefreshPromise = null;
          }
        }

        // Add authentication token if available
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
        } else {
          console.warn('[API] No auth token available for request:', config.url);
        }

        // Add request timestamp for debugging
        (config as InternalAxiosRequestConfig & { metadata?: { startTime: Date } }).metadata = { startTime: new Date() };

        return config;
      },
      (error) => {
        return Promise.reject(transformError(error));
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error) => {
        const apiError = transformError(error);
        const config = error.config as RequestConfig;

        // Retry logic
        if (config && config.retry !== false) {
          const retryCount = config.retryCount || 0;
          const maxRetries = 3;

          if (shouldRetry(apiError, retryCount, maxRetries)) {
            const delay = getRetryDelay(retryCount);
            console.warn(
              `[API] Retrying request (attempt ${retryCount + 1}/${maxRetries}) after ${delay}ms`
            );

            // Wait for delay
            await new Promise((resolve) => setTimeout(resolve, delay));

            // Increment retry count
            config.retryCount = retryCount + 1;

            // Retry the request
            return this.axiosInstance.request(config);
          }
        }

        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Configure the API client
   */
  public configure(config: ApiClientConfig): void {
    this.config = { ...this.config, ...config };

    // Update axios instance config
    if (config.baseURL) {
      this.axiosInstance.defaults.baseURL = config.baseURL;
    }
    if (config.timeout) {
      this.axiosInstance.defaults.timeout = config.timeout;
    }
    if (config.headers) {
      this.axiosInstance.defaults.headers.common = {
        ...this.axiosInstance.defaults.headers.common,
        ...config.headers,
      };
    }
    if (config.withCredentials !== undefined) {
      this.axiosInstance.defaults.withCredentials = config.withCredentials;
    }
  }

  /**
   * Set authentication token
   */
  public setAuthToken(token: string): void {
    this.authToken = token;
    // Also store in localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('medsuite_auth_token', token);
    }
  }

  /**
   * Clear authentication token
   */
  public clearAuthToken(): void {
    this.authToken = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('medsuite_auth_token');
    }
  }

  /**
   * Set token refresh callback
   * This callback will be invoked before each API request to get a fresh token
   * MSAL's acquireTokenSilent should be used as the callback for automatic token refresh
   */
  public setTokenRefreshCallback(callback: TokenRefreshCallback | null): void {
    this.tokenRefreshCallback = callback;
  }

  /**
   * Get current authentication token
   */
  public getAuthToken(): string | null {
    if (!this.authToken && typeof window !== 'undefined') {
      // Try to restore from localStorage
      this.authToken = localStorage.getItem('medsuite_auth_token');
    }
    return this.authToken;
  }

  /**
   * GET request
   */
  public async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get<T>(url, config);
    return this.transformResponse(response);
  }

  /**
   * POST request
   */
  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return this.transformResponse(response);
  }

  /**
   * PUT request
   */
  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return this.transformResponse(response);
  }

  /**
   * PATCH request
   */
  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.patch<T>(url, data, config);
    return this.transformResponse(response);
  }

  /**
   * DELETE request
   */
  public async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return this.transformResponse(response);
  }

  /**
   * Transform Axios response to ApiResponse
   */
  private transformResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  /**
   * Get the underlying Axios instance (for advanced usage)
   */
  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

/**
 * Singleton instance
 */
export const apiClient = new ApiClient();

/**
 * Export ApiClient class for creating custom instances
 */
export { ApiClient };
